import { EMessageResponseTypes, PopupManager, PushManager } from '@cian/push-manager';

import * as React from 'react';

import { useApplicationContext } from '../../context/ApplicationContext';

interface IUsePushManager {
  subscribeToPushAvailable: boolean;
  handlePushSubscribe: () => Promise<void>;
}

export function usePushManager(isOpen: boolean, handleOpen: () => void): IUsePushManager {
  const {
    config,
    logger,
    httpApi,
    custom: { subdomain },
  } = useApplicationContext();

  const pushBrowserUrl = config.get('push.browserUrl') as string;
  const applicationServerKey = config.get<string>('applicationServerKey') || '';
  const pushManager = React.useRef<PushManager>();
  const [pushPermission, setPushPermission] = React.useState<NotificationPermission>('default');
  const [isSubscribed, setIsSubscribed] = React.useState(false);

  const subscribeToPushAvailable = React.useMemo(
    () => !isSubscribed && pushPermission !== 'denied',
    [isSubscribed, pushPermission],
  );

  const handlePushSubscribe = React.useCallback(async () => {
    if (subdomain !== 'www') {
      if (pushManager.current) {
        pushManager.current.showPopup({
          baseUrl: 'web-push-subscribe-popup',
          trackingLabel: 'From_cat_save',
        });
      }
    } else if (applicationServerKey) {
      const popup = new PopupManager({
        workerSrc: '/push-worker.js',
        applicationServerKey,
        httpApi,
      });

      try {
        await popup.register();
      } catch (error) {
        logger.warning(error, { domain: 'shared.filters.containers.SaveSearchModalContainer.PopupManager.register' });

        return;
      }

      try {
        await popup.subscribe();
      } catch (error) {
        logger.warning(error, { domain: 'shared.filters.containers.SaveSearchModalContainer.PopupManager.subscribe' });

        return;
      }
    }
  }, [applicationServerKey, subdomain, httpApi, logger]);

  const handlePushManagerMessage = React.useCallback((e: MessageEvent) => {
    try {
      const data = JSON.parse(e.data);

      if (data.type === EMessageResponseTypes.subscriptionStatus && !data.value) {
        setIsSubscribed(data.value);
      }
      // eslint-disable-next-line no-empty
    } catch {}
  }, []);

  React.useEffect(() => {
    if (isOpen) {
      pushManager.current = new PushManager({
        baseUrl: pushBrowserUrl,
        hasSubdomain: subdomain !== 'www',
      });

      pushManager.current.registerWorker();
      PushManager.ready().then(PushManager.checkSubscription);

      if ('Notification' in window && subdomain === 'www') {
        setPushPermission(Notification.permission);
      }

      window.addEventListener('message', handlePushManagerMessage);

      handleOpen();
    }

    return (): void => window.removeEventListener('message', handlePushManagerMessage);
  }, [subdomain, handleOpen, handlePushManagerMessage, isOpen, pushBrowserUrl]);

  return { subscribeToPushAvailable, handlePushSubscribe };
}
